import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';

// ── Rectify (AI) (report 990a6f19) ───────────────────────────────────────────────────────────────────
// The reporter: "Allow an AI rectification tool in the email received to enable users to improve the quality of
// attachments received by clients … name this document correctly and ask the user whether or not they would like
// to store it if the AI amended version is acceptable." Opened from a JPEG, PNG or PDF attachment of an open email
// (the chip's wand, or the preview's Rectify (AI) button). The AI straightens the document — flat, all four corners
// showing, a thin white margin all round — judges it by the passports kept in Our Documents, and
// suggests its name by the naming protocol (server: services/rectifyDoc.ts). The original and the AI version side by
// side, the name in a box to edit, and "Store this version in the Drop Folder?": Accept files the flattened copy
// there; Reject, × or Esc throws it away. The email's own attachment is never changed.
// Opening #3 ("Allow screenshots in the body of the email to be rectified as well as attachments"): the same window
// also opens from the wand HtmlFrame puts on a picture in the body, and then asks the server's body-picture doors.
// Report 6cc0de58 ("allow users to rectify images in the chat app - on desktop and phone"): this window is no longer
// the email's own. It is handed the server calls to make (`doors`) instead of working them out from an email,
// so the Chat pop-up opens the very same window for a photo sent in a chat and passes its own — everything
// below, the naming protocol and the Drop Folder included, is then shared by both screens.
// Report 73922b6a, opening #12 ("You offer a tool like Perspective Fix, where you can deskew and straighten images by
// grabbing the corners of the image itself?"): the Original pane now carries four draggable corner handles and the
// quad they draw, set where the AI put the document's corners, and Straighten pulls the photo out by the quad
// the user leaves them in (doors.restraighten) and puts that copy in the other pane. The handles take a finger as
// well as a mouse, because this window opens on a phone from the chat. Accept, Reject and Esc work on whichever
// version is in that pane.
// Opening #13 ("it uses the ability to pick the corners that they want to use rather than automatically selecting
// them"): picking the corners is the FIRST step, as Perspective Fix has it. The handles are live the moment the
// window opens — on the AI's corners where it placed them, on the picture's own frame where it did not — and the
// AI's crop only stands beside them as a suggestion. A picture it found no document in is no longer a dead end: the
// photo opens with the handles on it, the other pane asks for the four corners, and Straighten makes the first copy.
// Accept waits until there is a copy to file.

type Ready = Awaited<ReturnType<typeof api.rectifyAttachment>>;

/** What is straightened: one of the email's attachments, or the n-th picture its body draws from a data: URI — a
 *  screenshot pasted into the mail, which has no attachment of its own (HtmlFrame numbers them, from 0). */
export type RectifyTarget = { att: { id: string; name: string } } | { image: number };

/** The four server calls this window makes: straighten it, straighten it again by the user's own four corners, keep
 *  the version on screen, throw it away. The email's are doorsOf below; the Chat pop-up passes its message's own
 *  (ChatPanel). */
export type Doors = {
  rectify: (lang: string) => Promise<Ready>;
  restraighten: (token: string, corners: number[][]) => Promise<{ ok: boolean; code?: string; image?: string }>;
  store: (token: string, name: string) => Promise<{ ok: boolean; code?: string; name?: string }>;
  discard: (token: string) => Promise<any>;
};

// The server's four calls for an EMAIL target: the attachment's, or the body picture's.
export function doorsOf(emailId: string, target: RectifyTarget): Doors {
  if ('att' in target) {
    const attId = target.att.id;
    return {
      rectify: lang => api.rectifyAttachment(emailId, attId, lang),
      restraighten: (token, corners) => api.restraightenAttachment(emailId, attId, token, corners),
      store: (token, name) => api.storeRectified(emailId, attId, token, name),
      discard: token => api.discardRectified(emailId, attId, token),
    };
  }
  const n = target.image;
  return {
    rectify: lang => api.rectifyBodyImage(emailId, n, lang),
    restraighten: (token, corners) => api.restraightenBodyImage(emailId, n, token, corners),
    store: (token, name) => api.storeRectifiedBodyImage(emailId, n, token, name),
    discard: token => api.discardRectifiedBodyImage(emailId, n, token),
  };
}

/** A JPEG, PNG or PDF — by the type the email gave it or by its name, as the preview judges a file. */
export function canRectify(type: string, name: string): boolean {
  const ct = (type || '').toLowerCase().split(';')[0].trim();
  const ext = (/\.([a-z0-9]+)$/i.exec(name || '')?.[1] || '').toLowerCase();
  return ct === 'application/pdf' || /^image\/(jpe?g|png)$/.test(ct) || ['pdf', 'jpg', 'jpeg', 'png'].includes(ext);
}

// The server's codes for an everyday outcome, to the words shown.
const FAIL_KEY: Record<string, string> = {
  unsupported: 'eml_rect_unsupported', too_large: 'eml_rect_too_large', unreadable: 'eml_rect_unreadable',
  no_document: 'eml_rect_no_document', expired: 'eml_rect_expired', bad_name: 'eml_rect_bad_name',
  no_copy: 'eml_rect_place_first', no_drop_folder: 'eml_rect_no_drop', store_failed: 'eml_rect_store_failed',
};
const AMBER = '#BA7517';
const RED = '#E24B4A';
// A corner handle: the circle that shows, and the square a finger or a mouse may press to take hold of it.
const DOT = 13;
const GRAB = 30;
const inPic = (v: number) => Math.max(0, Math.min(1, v));   // a handle never leaves the picture it sits on
// Where the handles start when the AI placed no corners of its own: the picture's own four, to be walked in onto the
// document (opening #13). Top-left first and clockwise, as everything here counts them.
const FRAME = [[0, 0], [1, 0], [1, 1], [0, 1]];

/**
 * `doors` = the server calls to make (the email's, or a chat message's); `fileName` the name under the heading, ''
 * for a picture that has none of its own; `z` how high the window stands, which the Chat pop-up raises because it
 * sits over the page itself.
 */
export default function RectifyModal({ doors: asked, fileName = '', z = 5100, onClose }:
  { doors: Doors; fileName?: string; z?: number; onClose: () => void }) {
  const { t } = useTranslation();
  const uiLang = useStore(s => s.uiLang);
  const showToast = useStore(s => s.showToast);
  // One window is for one picture (its opener keys it by that), so its calls are fixed as it opens.
  const [doors] = useState(() => asked);
  const [ready, setReady] = useState<Ready | null>(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // The straightened copy in the right-hand pane: the AI's to begin with where it made one, then whatever Straighten
  // last made. '' until there is one at all. `mine` only says which of the two it is, for that pane's own label.
  const [image, setImage] = useState('');
  const [mine, setMine] = useState(false);
  const [again, setAgain] = useState(false);
  const [againError, setAgainError] = useState('');
  // The four corner handles, as fractions of the Original picture, top-left first and clockwise — the AI's own where
  // it placed them, the picture's own frame where it did not (opening #13).
  const [quad, setQuad] = useState<number[][]>(FRAME);
  // The token of the copy waiting on the server until it is stored or thrown away. `started` keeps React's
  // development double run from paying for a second AI call.
  const token = useRef('');
  const started = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    doors.rectify(uiLang)
      .then(r => {
        if (!r.ok || !r.token) { if (alive.current) setError(t(FAIL_KEY[r.code || ''] || 'eml_rect_failed')); return; }
        // Closed while the AI was at work: nobody is left to answer, so the copy goes.
        if (!alive.current) { void doors.discard(r.token).catch(() => {}); return; }
        token.current = r.token;
        setReady(r);
        setName(r.name || '');
        setImage(r.image || '');
        // Clamped onto the picture: the AI may put a corner of a document that runs off the photo just outside it,
        // and a handle out there could not be seen or taken hold of. With no corners from it — it found no document —
        // the handles stand on the picture's own frame for the user to walk in.
        const c = r.corners;
        setQuad(Array.isArray(c) && c.length === 4 && c.every(p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite))
          ? c.map(([x, y]) => [inPic(x), inPic(y)]) : FRAME);
      })
      .catch((e: any) => {
        if (!alive.current) return;
        // A proxy's own error page (nginx's 504) is markup, not a reason — req() flags it htmlPage.
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setError(t('eml_rect_failed') + (why ? ` — ${why}` : ''));
      });
  }, [doors, uiLang, t]);

  // Reject, × and Esc: the copy waiting on the server is thrown away.
  const close = () => {
    if (token.current) void doors.discard(token.current).catch(() => {});
    token.current = '';
    onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  // Escape is caught on the way DOWN and stopped there: the window this one may be standing over listens for
  // Escape too — the Chat pop-up closes itself on it — and without this that window would close underneath
  // instead of this one closing (report 6cc0de58).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // ── The corner handles over the Original pane (opening #12) ──────────────────────────────────────────────
  // Where the picture itself is drawn inside its gray ground: the <img> fills the ground bar a 12px inset and holds
  // the picture centred inside that box by objectFit: contain, so the handles go by the PICTURE's rectangle, not the
  // box's, or a wide photo in a tall pane would put them out on the gray. Measured again whenever the pane changes
  // size (the window follows the browser's, and on a phone the two panes stand one above the other).
  const groundRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const boxRef = useRef(box);
  boxRef.current = box;
  const measure = useCallback(() => {
    const el = groundRef.current, im = imgRef.current;
    if (!el || !im || !im.naturalWidth || !im.naturalHeight) return;
    const gr = el.getBoundingClientRect(), ir = im.getBoundingClientRect();
    const s = Math.min(ir.width / im.naturalWidth, ir.height / im.naturalHeight);
    const w = im.naturalWidth * s, h = im.naturalHeight * s;
    const next = { x: ir.left - gr.left + (ir.width - w) / 2, y: ir.top - gr.top + (ir.height - h) / 2, w, h };
    setBox(p => (p && Math.abs(p.x - next.x) < 0.5 && Math.abs(p.y - next.y) < 0.5
      && Math.abs(p.w - next.w) < 0.5 && Math.abs(p.h - next.h) < 0.5 ? p : next));
  }, []);
  useLayoutEffect(() => {
    const el = groundRef.current;
    if (!ready || !el) return;   // the pane itself is only there once the AI has answered
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, ready]);

  // A handle follows the pointer — a mouse, a finger or a pen alike — for as long as it is held, the pointer captured
  // so the drag does not stop at the edge of the handle; and it stays on the picture, however far out the pointer goes.
  const held = useRef(-1);
  const grab = (i: number) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (again) return;
    e.preventDefault();
    e.stopPropagation();
    held.current = i;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the drag still follows the pointer over the picture */ }
  };
  const follow = (e: React.PointerEvent<HTMLDivElement>) => {
    const i = held.current, b = boxRef.current, el = groundRef.current;
    if (i < 0 || !b || !el || !(b.w > 0) || !(b.h > 0)) return;
    const gr = el.getBoundingClientRect();
    const x = inPic((e.clientX - gr.left - b.x) / b.w), y = inPic((e.clientY - gr.top - b.y) / b.h);
    setQuad(q => q.map((p, n) => (n === i ? [x, y] : p)));
    setAgainError(p => (p ? '' : p));   // the last refusal was about the quad they have just moved
  };
  const letGo = (e: React.PointerEvent<HTMLDivElement>) => {
    held.current = -1;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Straighten: the photo pulled out by the quad the handles are in now — the first copy where the AI made none, and
  // in place of the copy waiting on the server where it did, so Accept files this one. The name, the AI's verdict and
  // the original stay as they are.
  const straighten = () => {
    if (!ready || !ready.token || again) return;
    setAgain(true);
    setAgainError('');
    doors.restraighten(ready.token, quad)
      .then(r => {
        if (!alive.current) return;
        if (!r.ok || !r.image) {
          setAgainError(t(r.code === 'expired' ? 'eml_rect_expired' : r.code === 'no_document' ? 'eml_rect_again_quad' : 'eml_rect_again_failed'));
          return;
        }
        setImage(r.image);
        setMine(true);
        setSaveError('');
      })
      .catch((e: any) => {
        if (!alive.current) return;
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setAgainError(t('eml_rect_again_failed') + (why ? ` — ${why}` : ''));
      })
      .finally(() => { if (alive.current) setAgain(false); });
  };

  // Only once there is a straightened copy to file — and not while Straighten is at work: that run is making, or
  // replacing, the very copy this would file.
  const accept = () => {
    if (!ready || !ready.token || !image || saving || again) return;
    setSaving(true);
    setSaveError('');
    doors.store(ready.token, name)
      .then(r => {
        if (!alive.current) return;
        if (!r.ok) { setSaveError(t(FAIL_KEY[r.code || ''] || 'eml_rect_store_failed')); return; }
        token.current = '';   // stored: nothing left to throw away
        showToast(t('eml_rect_saved', { name: r.name || name }));
        onClose();
      })
      .catch((e: any) => {
        if (!alive.current) return;
        const why = e?.htmlPage ? '' : String(e?.message || '');
        setSaveError(t('eml_rect_store_failed') + (why ? ` — ${why}` : ''));
      })
      .finally(() => { if (alive.current) setSaving(false); });
  };

  const button = (filled: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)',
    border: filled ? 'none' : '1px solid var(--border)', background: filled ? 'var(--accent)' : 'transparent',
    color: filled ? '#fff' : 'var(--text-secondary)', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, flexShrink: 0,
  });

  // One picture, labelled, on a gray ground so the AI version's white margin shows. The picture is held 12px in from
  // all four sides of the ground, so a tall one (a passport's open booklet) is drawn whole and never cut off at its
  // foot.
  // 200px is the narrowest a picture is shown at: on a phone, where the chat's wand opens this window, the two
  // panes have no room side by side and the AI version wraps under the original instead of both being squeezed.
  const paneBox: React.CSSProperties = { flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 6 };
  const paneCaption: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
  const paneGround: React.CSSProperties = { flex: 1, minHeight: 0, position: 'relative', background: 'var(--c-gray-200)', borderRadius: 'var(--radius-md)', overflow: 'hidden' };
  const panePic: React.CSSProperties = { position: 'absolute', inset: 12, maxWidth: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', margin: 'auto', objectFit: 'contain', border: '1px solid var(--border)' };
  // The straightened copy beside the photo: the AI's own crop as the suggestion it starts from, then the one the
  // user's corners made. Until there is either, the pane asks for the four corners instead of standing empty.
  const copyPane = () => {
    const text = t(image ? (mine ? 'eml_rect_by_corners' : 'eml_rect_ai_version') : 'eml_rect_straightened');
    return (
      <div style={paneBox}>
        <div style={paneCaption}>{text}</div>
        <div style={paneGround}>
          {image ? <img src={image} alt={text} style={panePic} /> : (
            <div style={{ position: 'absolute', inset: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              {t('eml_rect_place_first')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // The Original, with a handle on each of the document's four corners and the quad they draw between them: take hold
  // of one and move it onto the real corner, then Straighten. The quad is drawn with a white line under the
  // green one so it shows over a dark photo as well as a pale one, and takes no presses itself — only the handles do.
  // Where the picture is drawn is read from `box`, which is 0 until it has been measured.
  const px = (p: number[]) => [(box?.x || 0) + p[0] * (box?.w || 0), (box?.y || 0) + p[1] * (box?.h || 0)];
  const corner = (p: number[], i: number) => (
    <div key={i} onPointerDown={grab(i)} onPointerMove={follow} onPointerUp={letGo} onPointerCancel={letGo}
      title={t('eml_rect_corner')} aria-label={t('eml_rect_corner')}
      style={{
        position: 'absolute', left: px(p)[0], top: px(p)[1],
        width: GRAB, height: GRAB, marginLeft: -GRAB / 2, marginTop: -GRAB / 2, display: 'flex', alignItems: 'center',
        justifyContent: 'center', touchAction: 'none', userSelect: 'none', cursor: again ? 'default' : 'grab',
      }}>
      <div style={{ width: DOT, height: DOT, borderRadius: '50%', background: 'var(--accent)', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
  const originalPane = () => (
    <div style={paneBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ ...paneCaption, flexShrink: 0 }}>{t('eml_rect_original')}</div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('eml_rect_drag')}</div>
      </div>
      <div ref={groundRef} style={paneGround}>
        <img ref={imgRef} src={ready?.original || ''} alt={t('eml_rect_original')} draggable={false} onLoad={measure} style={panePic} />
        {box && (
          <>
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden>
              {[{ c: '#fff', w: 3.5, o: 0.8 }, { c: 'var(--accent)', w: 1.5, o: 1 }].map((line, i) => (
                <polygon key={i} points={quad.map(p => px(p).join(',')).join(' ')}
                  fill={i ? 'var(--accent)' : 'none'} fillOpacity={0.1} stroke={line.c} strokeOpacity={line.o} strokeWidth={line.w} />
              ))}
            </svg>
            {quad.map(corner)}
          </>
        )}
      </div>
      {/* The step the window waits on until there is a copy, so it is the filled button while that is so. */}
      <button onClick={straighten} disabled={again}
        style={{ ...button(!image), alignSelf: 'flex-start', opacity: again ? 0.6 : 1, cursor: again ? 'default' : 'pointer' }}>
        <i className={`ti ${again ? 'ti-loader-2' : 'ti-refresh'}`} style={{ fontSize: 13, animation: again ? 'spin 1s linear infinite' : undefined }} aria-hidden />
        {t('eml_rect_straighten')}
      </button>
      {againError && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: RED }}>
          <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{againError}
        </div>
      )}
    </div>
  );

  return (
    <div data-rectify style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: ready ? 'min(1100px, 100%)' : 'min(440px, 100%)', height: ready ? '100%' : undefined, maxHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <i className="ti ti-wand" style={{ fontSize: 20, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t('eml_rect_btn')}</div>
            <div title={fileName} style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileName}</div>
          </div>
          <button onClick={close} title={t('eml_att_close')} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden />
          </button>
        </div>

        {error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '30px 24px 28px', textAlign: 'center' }}>
            <i className="ti ti-file-alert" style={{ fontSize: 44, color: RED }} aria-hidden />
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{error}</div>
            <button onClick={close} style={button(false)}>{t('eml_att_close')}</button>
          </div>
        ) : !ready ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '34px 24px 32px' }}>
            <i className="ti ti-loader-2" style={{ fontSize: 26, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('eml_rect_working')}</div>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexWrap: 'wrap', gap: 14, padding: '14px 18px' }}>
              {originalPane()}
              {copyPane()}
            </div>
            <div style={{ flexShrink: 0, padding: '12px 18px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* The AI's verdict is on the copy the AI itself made: shown while there is one, and not at all where
                  it found no document — there the note says so until the user's own corners make the first copy. */}
              {!image && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, fontWeight: 600, color: AMBER }}>
                  <i className="ti ti-alert-triangle" style={{ fontSize: 15, flexShrink: 0 }} aria-hidden />
                  <span>{t('eml_rect_no_document')}</span>
                </div>
              )}
              {!!ready.image && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, fontWeight: 600, color: ready.acceptable ? 'var(--accent)' : AMBER }}>
                  <i className={`ti ${ready.acceptable ? 'ti-circle-check' : 'ti-alert-triangle'}`} style={{ fontSize: 15, flexShrink: 0 }} aria-hidden />
                  <span>
                    {t(ready.acceptable ? 'eml_rect_acceptable' : 'eml_rect_not_acceptable')}
                    {!ready.acceptable && (ready.problems || []).length > 0 && <span style={{ fontWeight: 400 }}> — {(ready.problems || []).join('; ')}</span>}
                  </span>
                </div>
              )}
              {(ready.pages || 1) > 1 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('eml_rect_first_page')}</div>}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                <span style={{ flexShrink: 0 }}>{t('eml_rect_name')}</span>
                <input value={name} onChange={e => { setName(e.target.value); setSaveError(''); }} placeholder={t('eml_rect_name_hint')}
                  style={{ flex: 1, minWidth: 0, padding: '7px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />
                <span style={{ flexShrink: 0, fontWeight: 400, color: 'var(--text-tertiary)' }}>{ready.ext}</span>
              </label>
              {saveError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: RED }}>
                  <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{saveError}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t('eml_rect_ask')}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('eml_rect_kept')}</div>
                </div>
                <button onClick={close} disabled={saving} style={button(false)}>
                  <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden />{t('eml_rect_reject')}
                </button>
                {/* Nothing to file until a copy has been straightened. */}
                <button onClick={accept} disabled={saving || again || !image} title={image ? undefined : t('eml_rect_place_first')}
                  style={{ ...button(true), opacity: saving || !image ? 0.6 : 1, cursor: saving || !image ? 'default' : 'pointer' }}>
                  <i className={`ti ${saving ? 'ti-loader-2' : 'ti-check'}`} style={{ fontSize: 13, animation: saving ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('eml_rect_accept')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
